import { useEffect, useMemo, useRef, useState } from "react";
import { fmtMoney, vehicleLabel as fmtVehicleLabel } from "@/lib/format";
import { CaretDown, MagnifyingGlass, X } from "@phosphor-icons/react";

function vehicleLabel(v) {
  return fmtVehicleLabel(v);
}

/** Match a vehicle against the user's search query — case-insensitive, scans
 *  VIN, package name, year, color, engine number. Partial matches OK so users
 *  can type the tail of a VIN. */
function vehicleMatches(v, q) {
  if (!q) return true;
  const needle = q.trim().toLowerCase();
  const hay = [
    v.vin,
    vehicleLabel(v),
    v.year,
    v.color,
    v.engine_no,
    v.chassis_no,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return hay.includes(needle);
}

export function SaleVehicleSelect({ vehicles, packages, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const selectedVehicle = vehicles.find((v) => v.id === value);
  // Build a quick lookup so each VIN row can show its attached package name(s)
  // — much more recognisable than the generic "TVS SCOOTERS" make+model.
  const packageMap = useMemo(() => {
    const m = {};
    (packages || []).forEach((p) => { m[p.id] = p; });
    return m;
  }, [packages]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  useEffect(() => {
    if (open) {
      // Focus the search field as soon as the dropdown opens.
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setQ("");
    }
  }, [open]);

  const filtered = useMemo(() => {
    const list = vehicles.filter((v) => vehicleMatches(v, q));
    // Hard cap for render performance with very large inventories.
    return list.slice(0, 200);
  }, [vehicles, q]);

  const pick = (v) => {
    onChange(v.id, v);
    setOpen(false);
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange("", null);
  };

  return (
    <div ref={wrapRef}>
      <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">
        Vehicle
      </label>
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          data-testid="sale-vehicle"
          className={`w-full px-3 py-2 bg-canvas border text-left text-sm flex items-center justify-between gap-2 ${
            open ? "border-ink" : "border-canvas-line"
          } hover:border-ink`}
        >
          <span className={`truncate ${selectedVehicle ? "text-ink" : "text-ink-subtle"}`}>
            {selectedVehicle
              ? `${vehicleLabel(selectedVehicle)} — ${selectedVehicle.vin}`
              : "Select an available vehicle…"}
          </span>
          <span className="flex items-center gap-1 flex-shrink-0">
            {selectedVehicle && (
              <span
                role="button"
                tabIndex={0}
                onClick={clear}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") clear(e);
                }}
                data-testid="sale-vehicle-clear"
                className="p-1 hover:bg-canvas-panel cursor-pointer"
                aria-label="Clear vehicle"
              >
                <X size={11} weight="bold" />
              </span>
            )}
            <CaretDown size={11} weight="bold" />
          </span>
        </button>

        {open && (
          <div
            data-testid="sale-vehicle-listbox"
            className="absolute z-50 left-0 right-0 top-full mt-1 bg-canvas-card border border-ink shadow-lg max-h-[320px] flex flex-col"
          >
            <div className="px-2 py-2 border-b border-canvas-line flex items-center gap-2 bg-canvas-panel">
              <MagnifyingGlass size={12} weight="bold" className="text-ink-muted flex-shrink-0" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search VIN, model, color…"
                data-testid="sale-vehicle-search"
                className="flex-1 px-1 py-1 bg-transparent text-sm focus:outline-none"
              />
              {q && (
                <button
                  type="button"
                  onClick={() => setQ("")}
                  data-testid="sale-vehicle-search-clear"
                  className="p-1 hover:bg-canvas-card"
                  aria-label="Clear search"
                >
                  <X size={11} weight="bold" />
                </button>
              )}
            </div>

            <div className="overflow-y-auto flex-1">
              {filtered.length === 0 && (
                <div
                  data-testid="sale-vehicle-empty"
                  className="px-3 py-6 text-center font-mono text-xs text-ink-subtle"
                >
                  {vehicles.length === 0
                    ? "No available vehicles in stock."
                    : "No vehicles match your search."}
                </div>
              )}
              {filtered.map((v) => {
                const selected = v.id === value;
                const attachedPkgs = (v.package_ids || []).map((id) => packageMap[id]).filter(Boolean);
                const pkgLabel = attachedPkgs.map((p) => p.name).join(" · ");
                return (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => pick(v)}
                    data-testid={`sale-vehicle-option-${v.id}`}
                    className={`block w-full text-left px-3 py-2 text-sm border-b border-canvas-line last:border-b-0 ${
                      selected ? "bg-canvas-panel" : "hover:bg-canvas-panel/60"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-ink truncate">
                        {vehicleLabel(v)}
                      </span>
                      <span className="font-mono text-[10px] text-ink-muted tabular-nums flex-shrink-0">
                        {fmtMoney(v.price)}
                      </span>
                    </div>
                    {(pkgLabel || v.color) && (
                      <div className="text-[11px] text-ink truncate mt-0.5">
                        {[pkgLabel, v.color].filter(Boolean).join(" · ")}
                      </div>
                    )}
                    <div className="font-mono text-[10px] text-ink-subtle uppercase tracking-wider mt-0.5 truncate">
                      VIN {v.vin}
                    </div>
                  </button>
                );
              })}
              {vehicles.filter((v) => vehicleMatches(v, q)).length > filtered.length && (
                <div className="px-3 py-2 font-mono text-[10px] text-ink-subtle border-t border-canvas-line bg-canvas-panel">
                  Showing first {filtered.length}. Narrow your search…
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {selectedVehicle && (
        <div className="mt-2 px-3 py-2 border border-canvas-line bg-canvas-panel font-mono text-[11px] text-ink-muted">
          Listed: {fmtMoney(selectedVehicle.price)} · {selectedVehicle.color || "—"} ·{" "}
          {selectedVehicle.mileage || 0} mi
        </div>
      )}
    </div>
  );
}

export function SaleCustomerSelect({ customers, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const selected = customers.find((c) => c.id === value);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 0);
    else setQ("");
  }, [open]);

  const customerMatches = (c, query) => {
    if (!query) return true;
    const needle = query.trim().toLowerCase();
    const hay = [c.name, c.phone, c.email, c.gstin].filter(Boolean).join(" ").toLowerCase();
    return hay.includes(needle);
  };

  const filtered = useMemo(() => {
    return customers.filter((c) => customerMatches(c, q)).slice(0, 200);
  }, [customers, q]);

  const pick = (c) => {
    onChange(c.id);
    setOpen(false);
  };
  const clear = (e) => {
    e.stopPropagation();
    onChange("");
  };

  return (
    <div ref={wrapRef}>
      <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">
        Customer
      </label>
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          data-testid="sale-customer"
          className={`w-full px-3 py-2 bg-canvas border text-left text-sm flex items-center justify-between gap-2 ${
            open ? "border-ink" : "border-canvas-line"
          } hover:border-ink`}
        >
          <span className={`truncate ${selected ? "text-ink" : "text-ink-subtle"}`}>
            {selected ? `${selected.name}${selected.phone ? ` — ${selected.phone}` : ""}` : "Select customer…"}
          </span>
          <span className="flex items-center gap-1 flex-shrink-0">
            {selected && (
              <span
                role="button"
                tabIndex={0}
                onClick={clear}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") clear(e);
                }}
                data-testid="sale-customer-clear"
                className="p-1 hover:bg-canvas-panel cursor-pointer"
                aria-label="Clear customer"
              >
                <X size={11} weight="bold" />
              </span>
            )}
            <CaretDown size={11} weight="bold" />
          </span>
        </button>

        {open && (
          <div
            data-testid="sale-customer-listbox"
            className="absolute z-50 left-0 right-0 top-full mt-1 bg-canvas-card border border-ink shadow-lg max-h-[320px] flex flex-col"
          >
            <div className="px-2 py-2 border-b border-canvas-line flex items-center gap-2 bg-canvas-panel">
              <MagnifyingGlass size={12} weight="bold" className="text-ink-muted flex-shrink-0" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name, phone, email…"
                data-testid="sale-customer-search"
                className="flex-1 px-1 py-1 bg-transparent text-sm focus:outline-none"
              />
              {q && (
                <button
                  type="button"
                  onClick={() => setQ("")}
                  data-testid="sale-customer-search-clear"
                  className="p-1 hover:bg-canvas-card"
                  aria-label="Clear search"
                >
                  <X size={11} weight="bold" />
                </button>
              )}
            </div>
            <div className="overflow-y-auto flex-1">
              {filtered.length === 0 && (
                <div
                  data-testid="sale-customer-empty"
                  className="px-3 py-6 text-center font-mono text-xs text-ink-subtle"
                >
                  {customers.length === 0
                    ? "Add a customer first from the Customers page."
                    : "No customers match your search."}
                </div>
              )}
              {filtered.map((c) => {
                const isSel = c.id === value;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => pick(c)}
                    data-testid={`sale-customer-option-${c.id}`}
                    className={`block w-full text-left px-3 py-2 text-sm border-b border-canvas-line last:border-b-0 ${
                      isSel ? "bg-canvas-panel" : "hover:bg-canvas-panel/60"
                    }`}
                  >
                    <div className="font-medium text-ink truncate">{c.name}</div>
                    <div className="font-mono text-[10px] text-ink-subtle mt-0.5 truncate">
                      {c.phone || "—"}
                      {c.email ? ` · ${c.email}` : ""}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
      {customers.length === 0 && (
        <p className="font-mono text-[11px] text-destructive mt-1">
          Add a customer first from the Customers page.
        </p>
      )}
    </div>
  );
}
